import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useLocation,
  useNavigate,
} from "react-router-dom";

import {
  Archive,
  Bell,
  CalendarDays,
  ClipboardList,
  ExternalLink,
  FilePenLine,
  FolderOpen,
  Gauge,
  GraduationCap,
  Inbox,
  KeyRound,
  LayoutGrid,
  Mail,
  Megaphone,
  MessageSquare,
  Newspaper,
  ShieldAlert,
  Tablet,
  UserSearch,
} from "lucide-react";

import { useAuth } from "../../context/AuthContext";
import { api } from "../../api";

import { PortalShell } from "./PortalShell";
import { CalendarPanel } from "./CalendarPanel";
import { NoticesPanel } from "./NoticesPanel";
import { StaffMessagesPanel } from "./StaffMessagesPanel";
import { EnquiriesPanel } from "./EnquiriesPanel";
import { RecruitmentPanel } from "./RecruitmentPanel";
import { CommsPanel } from "./CommsPanel";
import { NotificationsPanel } from "./NotificationsPanel";
import { DocumentsPanel } from "./DocumentsPanel";
import { BlogAdminPanel } from "./BlogAdminPanel";
import { PwaManager } from "./PwaManager";
import { SiteContentPanel } from "./SiteContentPanel";
import { SmsSupportPanel } from "./SmsSupportPanel";
import { TrainingPlanningPanel } from "./TrainingPlanningPanel";
import { CustomActivitiesPanel } from "./CustomActivitiesPanel";
import { InventoryPanel } from "./InventoryPanel";
import { HealthSafetyPanel } from "./HealthSafetyPanel";
import { CadetTrainingRecordsPanel } from "./CadetTrainingRecordsPanel";

const STAFF_TABS = [
  {
    key: "overview",
    label: "Overview",
    icon: Gauge,
  },

  {
    key: "calendar",
    label: "Events",
    icon: CalendarDays,
  },

  {
    key: "training",
    label: "Training Plan",
    icon: ClipboardList,
  },

  {
    key: "training_records",
    label: "Training Records",
    icon: GraduationCap,
  },

  {
    key: "inventory",
    label: "Stores",
    icon: Archive,
  },

  {
    key: "health_safety",
    label: "H&S",
    icon: ShieldAlert,
  },

  {
    key: "recruitment",
    label: "Recruitment",
    icon: UserSearch,
  },

  {
    key: "enquiries",
    label: "Enquiries",
    icon: Inbox,
  },

  {
    key: "comms",
    label: "Communications",
    icon: Megaphone,
  },

  {
    key: "notices",
    label: "Notices",
    icon: Bell,
  },

  {
    key: "documents",
    label: "Documents",
    icon: FolderOpen,
  },

  {
    key: "news",
    label: "News",
    icon: Newspaper,
  },

  {
    key: "messages",
    label: "Messages",
    icon: MessageSquare,
  },

  {
    key: "inbox",
    label: "Inbox",
    icon: Mail,
  },

  {
    key: "website",
    label: "Website",
    icon: FilePenLine,
  },

  {
    key: "activities",
    label: "Activities",
    icon: LayoutGrid,
  },

  {
    key: "support",
    label: "Support",
    icon: KeyRound,
  },

  {
    key: "device",
    label: "This Device",
    icon: Tablet,
  },
];

const STAFF_NAV_GROUPS = [
  {
    label: "Home",
    tabs: ["overview"],
  },

  {
    label: "Operations",
    tabs: [
      "calendar",
      "training",
      "training_records",
      "inventory",
      "health_safety",
    ],
  },

  {
    label: "People",
    tabs: [
      "recruitment",
      "enquiries",
    ],
  },

  {
    label: "Communications",
    tabs: [
      "comms",
      "notices",
      "documents",
      "news",
      "messages",
      "inbox",
    ],
  },

  {
    label: "System",
    tabs: [
      "website",
      "activities",
      "support",
      "device",
    ],
  },
];

/*
 * Retained in case legacy CFAV login is temporarily enabled.
 * Normal staff-device sessions are mapped to admin.
 */
const NON_UNIFORMED_CFAV_KEYS = [
  "overview",
  "calendar",
  "training",
  "training_records",
  "inventory",
  "notices",
  "documents",
  "news",
  "messages",
  "inbox",
  "support",
  "device",
];

const APPOINTMENT_TAB_ACCESS = {
  training_officer: [
    "training",
    "training_records",
    "calendar",
    "documents",
    "messages",
    "inbox",
  ],

  adjutant: [
    "comms",
    "documents",
    "enquiries",
    "calendar",
    "messages",
    "inbox",
  ],

  stores_officer: [
    "inventory",
    "documents",
    "calendar",
    "messages",
    "inbox",
  ],

  oc: STAFF_TABS.map(
    (tab) => tab.key
  ),

  deputy_oc: STAFF_TABS.map(
    (tab) => tab.key
  ),

  health_safety_officer: [
    "health_safety",
    "calendar",
    "notices",
    "documents",
    "messages",
    "inbox",
  ],
};

function StaffOverview({
  user,
  unread,
  tabs,
  onNavigateTab,
}) {
  const quickKeys = [
    "calendar",
    "training",
    "training_records",
    "recruitment",
    "enquiries",
    "comms",
  ];

  const quickTabs =
    quickKeys
      .map((key) =>
        tabs.find(
          (tab) =>
            tab.key === key
        )
      )
      .filter(Boolean);

  return (
    <div className="space-y-5">
      {/* Welcome */}

      <div className="bg-raf-navy text-white rounded-xl p-5 md:p-7 border-b-4 border-raf-red">
        <p className="text-xs uppercase tracking-widest text-white/60 font-bold">
          1471 Horwich Squadron
        </p>

        <h1 className="text-2xl md:text-3xl font-display font-bold mt-2">
          Staff Portal
        </h1>

        <p className="text-white/75 mt-2 max-w-2xl">
          Events, training,
          recruitment,
          communications and
          squadron administration
          from one place.
        </p>

        {user?.auth_source ===
          "device" && (
          <div className="mt-5 inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm">
            <Tablet size={16} />

            {user.first_name ||
              user.device_code ||
              "Authorised staff device"}
          </div>
        )}
      </div>

      {/* Quick launch */}

      <div>
        <h2 className="font-display font-bold text-xl text-raf-navy">
          Quick access
        </h2>

        <p className="text-sm text-raf-slate mt-1">
          The functions staff use
          most often.
        </p>

        <div className="mt-4 grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {quickTabs.map(
            (tab) => {
              const Icon =
                tab.icon;

              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() =>
                    onNavigateTab(
                      tab.key
                    )
                  }
                  className="group bg-white border border-slate-200 rounded-xl p-4 text-left hover:border-raf-blue hover:shadow-sm transition-all"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 text-raf-navy flex items-center justify-center group-hover:bg-raf-navy group-hover:text-white transition-colors">
                      <Icon size={20} />
                    </div>

                    <div>
                      <h3 className="font-bold text-raf-navy">
                        {tab.label}
                      </h3>

                      <p className="text-xs text-slate-500 mt-1">
                        Open{" "}
                        {tab.label.toLowerCase()}
                      </p>
                    </div>
                  </div>
                </button>
              );
            }
          )}
        </div>
      </div>

      {/* Notifications */}

      {unread > 0 && (
        <button
          type="button"
          onClick={() =>
            onNavigateTab("inbox")
          }
          className="w-full bg-blue-50 border border-blue-200 rounded-xl p-4 text-left flex items-center gap-3"
        >
          <div className="w-10 h-10 bg-raf-blue text-white rounded-full flex items-center justify-center font-bold">
            {unread > 99
              ? "99+"
              : unread}
          </div>

          <div>
            <p className="font-bold text-raf-navy">
              You have unread
              inbox items
            </p>

            <p className="text-sm text-slate-600">
              Open the inbox to
              review email and squadron messages.
            </p>
          </div>
        </button>
      )}

      {/* RAFAC systems */}

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h2 className="font-display font-bold text-lg text-raf-navy">
          RAFAC systems
        </h2>

        <p className="text-sm text-slate-500 mt-1">
          Open official RAFAC
          systems in a separate tab.
          Login credentials are not
          stored or displayed by this
          website.
        </p>

        <div className="mt-4 flex flex-wrap gap-2">
          <a
            href="https://sms.bader.mod.uk/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-raf-red text-white rounded-lg px-4 py-2.5 font-semibold"
          >
            <ExternalLink
              size={16}
            />
            Open SMS
          </a>

          <a
            href="https://cadets.bader.mod.uk/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-raf-blue text-raf-blue rounded-lg px-4 py-2.5 font-semibold hover:bg-raf-blue hover:text-white transition-colors"
          >
            <ExternalLink
              size={16}
            />
            Open Cadet Portal
          </a>
        </div>
      </div>
    </div>
  );
}

function SupportPanel() {
  return (
    <div className="space-y-5">
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h1 className="font-display font-bold text-xl text-raf-navy">
          Support
        </h1>

        <p className="text-sm text-raf-slate mt-1">
          RAFAC system links and
          squadron SMS support.
        </p>

        <div className="mt-4">
          <a
            href="https://sms.bader.mod.uk/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-raf-red text-white rounded-lg px-4 py-2.5 font-semibold"
          >
            <ExternalLink
              size={16}
            />
            Open SMS
          </a>
        </div>
      </div>

      <SmsSupportPanel />
    </div>
  );
}

function DevicePanel({
  user,
}) {
  return (
    <div className="space-y-5">
      <div className="bg-white border border-slate-200 rounded-xl p-5 md:p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-raf-navy text-white flex items-center justify-center shrink-0">
            <Tablet size={23} />
          </div>

          <div>
            <h1 className="font-display font-bold text-xl text-raf-navy">
              This Staff Device
            </h1>

            <p className="text-sm text-raf-slate mt-1">
              Device-based access
              information for this
              computer.
            </p>
          </div>
        </div>

        <div className="mt-5 grid sm:grid-cols-2 gap-3">
          <div className="bg-slate-50 rounded-xl p-4">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-bold">
              Device
            </p>

            <p className="font-bold text-raf-navy mt-1">
              {user?.first_name ||
                "Staff Device"}
            </p>
          </div>

          <div className="bg-slate-50 rounded-xl p-4">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-bold">
              Device code
            </p>

            <p className="font-bold text-raf-navy mt-1">
              {user?.device_code ||
                user?.login_username ||
                "—"}
            </p>
          </div>
        </div>

        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-sm text-blue-900">
            This computer uses
            squadron device
            authentication. It does
            not require a personal
            staff username or
            password for this
            website.
          </p>
        </div>

        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-sm text-amber-900">
            Actions can currently be
            associated with this
            staff computer, rather
            than the individual staff
            member using it.
          </p>
        </div>
      </div>
    </div>
  );
}

export const Dashboard = () => {
  const { user } = useAuth();

  const location =
    useLocation();

  const navigate =
    useNavigate();

  const role =
    user?.role;

  const isAdmin =
    role === "admin";

  const isUniformedCfav =
    role === "cfav" &&
    Boolean(
      user?.is_uniformed
    );

  const visibleTabs =
    useMemo(() => {
      if (isAdmin) {
        return STAFF_TABS;
      }

      const appointmentKeys =
        Array.from(
          new Set(
            (
              user?.appointments ||
              []
            ).flatMap(
              (appointment) =>
                APPOINTMENT_TAB_ACCESS[
                  appointment
                ] || []
            )
          )
        );

      const allowed =
        new Set([
          "overview",
          "device",
          ...(isUniformedCfav
            ? STAFF_TABS
                .filter(
                  (tab) =>
                    ![
                      "website",
                      "activities",
                    ].includes(
                      tab.key
                    )
                )
                .map(
                  (tab) =>
                    tab.key
                )
            : NON_UNIFORMED_CFAV_KEYS),
          ...appointmentKeys,
        ]);

      return STAFF_TABS.filter(
        (tab) =>
          allowed.has(
            tab.key
          )
      );
    }, [
      isAdmin,
      isUniformedCfav,
      user?.appointments,
    ]);

  const getRequestedTab =
    useCallback(() => {
      const requested =
        new URLSearchParams(
          location.search
        ).get("tab");

      if (
        requested &&
        visibleTabs.some(
          (tab) =>
            tab.key ===
            requested
        )
      ) {
        return requested;
      }

      return (
        visibleTabs[0]?.key ||
        "overview"
      );
    }, [
      location.search,
      visibleTabs,
    ]);

  const [
    active,
    setActive,
  ] = useState(
    getRequestedTab
  );

  const [
    unread,
    setUnread,
  ] = useState(0);

  const loadUnread =
    useCallback(async () => {
      try {
        const [notificationsRes, emailRes] =
          await Promise.all([
            api.get(
              "/notifications/unread-count"
            ),
            api.get(
              "/email/unread-count"
            ),
          ]);

        const count =
          Number(
            notificationsRes.data?.count || 0
          ) +
          Number(
            emailRes.data?.count || 0
          );

        setUnread(count);

        if (
          "setAppBadge" in
            navigator
        ) {
          if (count > 0) {
            navigator.setAppBadge(
              count
            );
          } else if (
            navigator.clearAppBadge
          ) {
            navigator.clearAppBadge();
          }
        }
      } catch {
        // Notifications should never
        // prevent the dashboard loading.
      }
    }, []);

  useEffect(() => {
    loadUnread();

    const timer =
      setInterval(
        loadUnread,
        30000
      );

    return () =>
      clearInterval(timer);
  }, [loadUnread]);

  useEffect(() => {
    if (
      !visibleTabs.some(
        (tab) =>
          tab.key === active
      )
    ) {
      setActive(
        visibleTabs[0]?.key ||
          "overview"
      );
    }
  }, [
    active,
    visibleTabs,
  ]);

  useEffect(() => {
    const requested =
      getRequestedTab();

    if (
      requested !== active
    ) {
      setActive(requested);
    }
  }, [
    active,
    getRequestedTab,
  ]);

  const changeTab = (
    key
  ) => {
    if (
      !visibleTabs.some(
        (tab) =>
          tab.key === key
      )
    ) {
      return;
    }

    setActive(key);

    const params =
      new URLSearchParams(
        location.search
      );

    params.set(
      "tab",
      key
    );

    navigate(
      {
        pathname:
          location.pathname,
        search:
          params.toString()
            ? `?${params.toString()}`
            : "",
      },
      {
        replace: true,
      }
    );
  };

  const tabsWithBadge =
    visibleTabs.map(
      (tab) =>
        tab.key === "inbox" &&
        unread > 0
          ? {
              ...tab,
              badge:
                unread,
            }
          : tab
    );

  const renderPanel = () => {
    switch (active) {
      case "overview":
        return (
          <StaffOverview
            user={user}
            unread={unread}
            tabs={
              visibleTabs
            }
            onNavigateTab={
              changeTab
            }
          />
        );

      case "calendar":
        return (
          <CalendarPanel
            canManage={
              isAdmin
            }
            onNavigateTab={
              changeTab
            }
          />
        );

      case "training":
        return (
          <TrainingPlanningPanel />
        );

      case "training_records":
        return (
          <CadetTrainingRecordsPanel />
        );

      case "inventory":
        return (
          <InventoryPanel />
        );

      case "health_safety":
        return (
          <HealthSafetyPanel />
        );

      case "recruitment":
        return (
          <RecruitmentPanel />
        );

      case "enquiries":
        return (
          <EnquiriesPanel />
        );

      case "comms":
        return <CommsPanel />;

      case "notices":
        return (
          <NoticesPanel
            canManage
          />
        );

      case "documents":
        return (
          <DocumentsPanel />
        );

      case "news":
        return (
          <BlogAdminPanel />
        );

      case "messages":
        return (
          <StaffMessagesPanel />
        );

      case "inbox":
        return (
          <NotificationsPanel
            onRead={() => {
              loadUnread();
            }}
          />
        );

      case "website":
        return (
          <SiteContentPanel />
        );

      case "activities":
        return (
          <CustomActivitiesPanel />
        );

      case "support":
        return (
          <SupportPanel />
        );

      case "device":
        return (
          <DevicePanel
            user={user}
          />
        );

      default:
        return (
          <StaffOverview
            user={user}
            unread={unread}
            tabs={
              visibleTabs
            }
            onNavigateTab={
              changeTab
            }
          />
        );
    }
  };

  return (
    <PortalShell
      tabs={tabsWithBadge}
      active={active}
      onTab={changeTab}
      navGroups={
        STAFF_NAV_GROUPS
      }
    >
      <PwaManager />

      {renderPanel()}
    </PortalShell>
  );
};

export default Dashboard;